import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag } from 'antd';
import moment from 'moment';
import { getAuditTrailLogs } from '../../services/auditTrailService';
import AuditDetailModal from './AuditDetailModal';
import { parseChannel, channelTagColor } from './utils';

/**
 * Customer Activity Tab
 * Filters "All events" to show only those linked to customer actions
 * (registrations, logins, profile updates, transactions).
 */
const CUSTOMER_EVENT_TYPES = new Set([
  'USER_REGISTRATION',
  'USER_LOGIN',
  'USER_LOGOUT',
  'TRANSACTION',
  'BILL_PAYMENT',
  'UPDATE_PROFILE',
  'PASSWORD_CHANGE_SUCCESS',
  'OTP_REQUESTED',
  'OTP_VERIFIED_SUCCESS',
]);

const CustomerAuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchLogs = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      // Default view: Fetch all and filter client-side for customer types
      const res = await getAuditTrailLogs({ page: page - 1, size: 100 });
      if (res?.success) {
        const allContent = res.data?.content || [];
        const filtered = allContent.filter((e) => CUSTOMER_EVENT_TYPES.has(e.eventType));
        
        const start = (page - 1) * size;
        const pageContent = filtered.slice(start, start + size);
        
        setLogs(pageContent);
        setPagination({ current: page, pageSize: size, total: filtered.length });
      } else {
        setLogs([]);
      }
    } catch (err) {
      console.error('Failed to fetch customer audit logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id) => <span style={{ fontWeight: 600 }}>{id}</span>,
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (type) => (
        <Tag
          style={{
            background: '#e6f7ff',
            color: '#1890ff',
            border: '1px solid #91d5ff',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {type?.replace(/_/g, ' ') || '—'}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const isSuccess = status === 'SUCCESS';
        return (
          <Tag
            style={{
              color: isSuccess ? '#52c41a' : '#ff4d4f',
              background: isSuccess ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${isSuccess ? '#b7eb8f' : '#ffa39e'}`,
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Channel',
      dataIndex: 'userAgent',
      key: 'channel',
      render: (ua) => {
        const channel = parseChannel(ua);
        return (
          <Tag color={channelTagColor(channel)} style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}>
            {channel}
          </Tag>
        );
      },
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm A') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="audit-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({ onClick: () => handleRowClick(record) })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'audit-row-even' : 'audit-row-odd')}
          scroll={{ x: 'max-content' }}
          className="audit-table"
        />
      </div>
      <AuditDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        record={selectedLog}
      />
    </div>
  );
};

export default CustomerAuditTab;
